import { useEffect, useState } from "react";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api } from "@/lib/api";
import { prefetchAll } from "@/lib/offline";
import Sidebar from "@/components/Sidebar";
import ChatArea from "@/components/ChatArea";
import AISearchDialog from "@/components/AISearchDialog";
import EmptyState from "@/components/EmptyState";
import WallpaperBackdrop from "@/components/WallpaperBackdrop";
import { toast } from "sonner";
import { useWallpaperOnBody } from "@/lib/wallpaper";
import { initPushNotifications } from "@/lib/push";

export default function Workspace() {
  useWallpaperOnBody();
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const [titles, setTitles] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchScope, setSearchScope] = useState(null); // null = all notes, {title_id, name} = single thread
  const [jumpToItemId, setJumpToItemId] = useState(null);
  const [bootLoading, setBootLoading] = useState(true);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();

  // Deep link from a tapped reminder notification: /app?title=…&item=…
  useEffect(() => {
    const titleId = searchParams.get("title");
    const itemId = searchParams.get("item");
    if (!titleId && !itemId) return;
    if (titleId) setActiveId(titleId);
    if (itemId) setJumpToItemId(itemId);
    setSearchParams({}, { replace: true });
  }, [searchParams, setSearchParams]);

  // Return from Stripe checkout for an AI search pack.
  useEffect(() => {
    const sid = searchParams.get("search_pack_session");
    if (searchParams.get("search_pack_cancelled")) { toast.message("Search pack purchase cancelled"); setSearchParams({}, { replace: true }); return; }
    if (!sid) return;
    setSearchParams({}, { replace: true });
    api.get(`/search/buy/status/${sid}`).then((r) => {
      if (r.data.paid) { toast.success("Searches added to your account"); setSearchOpen(true); }
      else toast.message("Payment is still processing, your searches will appear shortly");
    }).catch(() => toast.error("Could not confirm your search pack payment"));
  }, [searchParams, setSearchParams]);


  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const r = await api.get("/titles");
        setTitles(r.data);
        if (r.data.length > 0) setActiveId((prev) => prev || r.data[0].title_id);
      } catch (e) {
        toast.error("Failed to load titles");
      } finally {
        setBootLoading(false);
      }
    })();
  }, [user]);

  // Register this device for OS-level push notifications (reminders delivered
  // even when NoteLosh is closed). Fires exactly once per login; no-op if the
  // browser doesn't support push or the user denies the permission prompt.
  useEffect(() => {
    if (!user) return;
    const t = setTimeout(() => { initPushNotifications(); }, 2000);
    return () => clearTimeout(t);
  }, [user]);

  // Cross-device trash sync: a note trashed on another device (or the web
  // app while this one was in the background) shouldn't keep showing here.
  // Re-pull the thread list whenever this device comes back to the
  // foreground, so a title that vanished elsewhere disappears for us too.
  useEffect(() => {
    if (!user) return;
    const sync = async () => {
      try {
        const r = await api.get("/titles");
        setTitles(r.data);
      } catch { /* background sync — stay quiet on failure */ }
    };
    const onVisible = () => { if (document.visibilityState === "visible") sync(); };
    window.addEventListener("focus", sync);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("focus", sync);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [user]);

  // If the thread currently open here was trashed elsewhere, drop off it.
  useEffect(() => {
    if (bootLoading || !activeId) return;
    if (!titles.some((t) => t.title_id === activeId)) {
      setActiveId(titles[0]?.title_id || null);
      toast.info("That note was moved to Trash on another device");
    }
  }, [titles, activeId, bootLoading]);

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setSearchScope(null);
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Swallow stray file drops that miss the chat dropzone so the browser
  // doesn't navigate away from the app and lose the user's session.
  useEffect(() => {
    const stop = (e) => {
      if (e.dataTransfer && Array.from(e.dataTransfer.types || []).includes("Files")) {
        e.preventDefault();
      }
    };
    window.addEventListener("dragover", stop);
    window.addEventListener("drop", stop);
    return () => {
      window.removeEventListener("dragover", stop);
      window.removeEventListener("drop", stop);
    };
  }, []);

  useEffect(() => {
    const onSynced = () => { api.get("/titles").then((r) => setTitles(r.data)).catch(() => {}); };
    window.addEventListener("notelosh:synced", onSynced);
    const t = user ? setTimeout(() => prefetchAll(api), 2000) : null;
    return () => { clearTimeout(t); window.removeEventListener("notelosh:synced", onSynced); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);

  if (loading || bootLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center text-[#9CA3AF] text-sm">
        Loading your workspace…
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;

  const refreshTitles = async () => {
    const r = await api.get("/titles");
    setTitles(r.data);
  };

  const createTitle = async (name, color) => {
    const r = await api.post("/titles", { name, color });
    setTitles((t) => [r.data, ...t]);
    setActiveId(r.data.title_id);
    return r.data;
  };

  const renameTitle = async (id, patch) => {
    // Accepts either a plain string (legacy) or an object { name, color }.
    const body = typeof patch === "string" ? { name: patch } : patch;
    const r = await api.patch(`/titles/${id}`, body);
    setTitles((t) => t.map((x) => (x.title_id === id ? r.data : x)));
  };

  const deleteTitle = async (id) => {
    await api.delete(`/titles/${id}`);
    setTitles((t) => t.filter((x) => x.title_id !== id));
    if (activeId === id) {
      const rest = titles.filter((x) => x.title_id !== id);
      setActiveId(rest[0]?.title_id || null);
    }
  };

  const reorderTitles = async (orderedIds) => {
    // Optimistic reorder — remap, then persist. On failure refetch.
    const byId = new Map(titles.map((t) => [t.title_id, t]));
    const next = orderedIds.map((id) => byId.get(id)).filter(Boolean);
    setTitles(next);
    try {
      await api.post("/titles/reorder", { title_ids: orderedIds });
    } catch {
      toast.error("Could not save new order");
      try { const r = await api.get("/titles"); setTitles(r.data); } catch { /* ignore */ }
    }
  };

  const handleSearchOpenTitle = ({ title_id, item_id }) => {
    setActiveId(title_id);
    setJumpToItemId(item_id || null);
    setSearchOpen(false);
    setMobileSidebarOpen(false);
  };

  const active = titles.find((t) => t.title_id === activeId) || null;

  // On mobile, tapping a title should close the drawer so chat becomes visible.
  const handleSelectTitle = (id) => {
    setActiveId(id);
    setMobileSidebarOpen(false);
  };

  const openGlobalSearch = () => {
    setSearchScope(null);
    setSearchOpen(true);
  };
  const openThreadSearch = () => {
    if (!active) { openGlobalSearch(); return; }
    setSearchScope({ title_id: active.title_id, name: active.name });
    setSearchOpen(true);
  };

  return (
    <div className="h-screen w-full flex overflow-hidden text-[#F3F4F6]">
      <WallpaperBackdrop />
      <Sidebar
        titles={titles}
        activeId={activeId}
        onSelect={handleSelectTitle}
        onCreate={createTitle}
        onRename={renameTitle}
        onDelete={deleteTitle}
        onOpenSearch={openGlobalSearch}
        onJumpToItem={handleSearchOpenTitle}
        onReorder={reorderTitles}
        mobileOpen={mobileSidebarOpen}
        onMobileClose={() => setMobileSidebarOpen(false)}
      />
      <main className="flex-1 flex flex-col min-w-0">
        {active ? (
          <ChatArea
            title={active}
            titles={titles}
            onActivityChange={refreshTitles}
            onOpenSearch={openThreadSearch}
            onOpenSidebar={() => setMobileSidebarOpen(true)}
            jumpToItemId={jumpToItemId}
            onJumpConsumed={() => setJumpToItemId(null)}
            onTitleUpdate={renameTitle}
            onOpenItem={handleSearchOpenTitle}
          />
        ) : (
          <EmptyState
            onCreate={createTitle}
            onOpenSidebar={() => setMobileSidebarOpen(true)}
          />
        )}
      </main>
      <AISearchDialog
        open={searchOpen}
        onOpenChange={setSearchOpen}
        onPickTitle={handleSearchOpenTitle}
        scope={searchScope}
      />
    </div>
  );
}
